import { useParams } from "react-router-dom";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { Button } from "~/components/ui/button";
import { useStepperContext } from "~/components/ui/stepper";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";
import { useIngestionContext } from "./context/IngestionContext";
import { FileValidationStepperState, ValidationMetadata } from "./types";
import { stripS3Prefix } from "./utils/jobParsers";

interface ValidationNavigationProps {
  onClose: () => void;
  onFileValidated: (file: FileInfo, validationMetadata: ValidationMetadata) => void;
  /** Called on the success path (Confirm & Submit) to set validated=true in URL */
  onConfirm?: () => void;
}

/** Handles step navigation, ingestion creation, and action buttons */
export const ValidationNavigation = ({
  onClose,
  onFileValidated,
  onConfirm,
}: ValidationNavigationProps) => {
  const { datasetId } = useParams();
  const { currentStep, setCurrentStep, stepperState } =
    useStepperContext<FileValidationStepperState>();
  const {
    selectedFile,
    pendingLocalFile,
    hasIngestion,
    canStartValidation,
    isLoading,
    isUploading,
    error,
    startIngestion,
    uploadPendingFile,
    clearError,
  } = useIngestionContext();

  const {
    isProfiling,
    dataProfile,
    profilingError,
    validationState,
    recoveredSourceFile,
    ingestion,
    isLoadingIngestion,
  } = stepperState;

  const isProfilingComplete = !isProfiling && !!dataProfile;
  const hasMajorFailure = validationState?.hasErrors || !!profilingError;
  const selectedFileMatchesIngestion =
    !!selectedFile &&
    (selectedFile.s3_uri === recoveredSourceFile ||
      selectedFile.key === recoveredSourceFile ||
      (!!ingestion?.source_file &&
        (selectedFile.s3_uri === ingestion.source_file ||
          selectedFile.key === ingestion.source_file)));
  const isStartingFresh =
    hasIngestion && (!!pendingLocalFile || (!!selectedFile && !selectedFileMatchesIngestion));

  const handleNext = async () => {
    clearError();
    trackEvent("validation_step_navigated", {
      dataset_id: datasetId,
      from_step: currentStep,
      to_step: currentStep + 1,
      direction: "forward",
      ingestion_id: ingestion?.data_ingestion_id,
    });

    if (currentStep === 0) {
      if (hasIngestion && !isStartingFresh) {
        // Resume existing ingestion
        setCurrentStep(1);
      } else if (pendingLocalFile) {
        // Upload flow: create ingestion, upload with visible progress, then advance
        const newIngestionId = await startIngestion();
        if (newIngestionId) {
          const uploaded = await uploadPendingFile();
          if (uploaded) {
            setCurrentStep(1);
          }
        }
      } else if (selectedFile) {
        // Cloud file: already in context via selectFile, create ingestion and advance
        const newIngestionId = await startIngestion();
        if (newIngestionId) {
          setCurrentStep(1);
        }
      }
    } else if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Use selectedFile if available, otherwise construct a minimal FileInfo
      // from the recovered source file (page refresh / re-confirm scenario).
      // key must be the plain storage key (no s3:// prefix) for display via resolveRouterPath.
      const fileForSubmit: FileInfo | null =
        selectedFile ??
        (recoveredSourceFile
          ? {
              key: stripS3Prefix(recoveredSourceFile) || recoveredSourceFile,
              type: FileType.FILE,
              s3_uri: recoveredSourceFile,
            }
          : null);

      if (!fileForSubmit) {
        console.error("ValidationNavigation: No file selected at summary step");
        return;
      }

      // Use the structured fileType from validation results to determine delimiter
      const fileType = validationState?.fileType || "csv";
      const delimiter = fileType === "tsv" ? "\t" : ",";

      const validationMetadata: ValidationMetadata = {
        validatedFilePath: validationState?.validatedFilePath,
        summary: validationState?.summary,
        dataProfile: dataProfile ?? undefined,
        file_format: {
          type: "csv", //SNOWFLAKE only supports CSV/TSV currently, and tsv is handled via delimiter
          delimiter: delimiter,
          encoding: "UTF8",
          null_if: ["\\N", "NULL", ""],
          skip_header: 1, // original file has a header
          skip_error_rows: true, // use ON_ERROR = CONTINUE during ingest
        },
      };
      trackEvent("file_validation_accepted", {
        dataset_id: datasetId,
        ingestion_id: ingestion?.data_ingestion_id,
        has_warnings: validationState?.hasWarnings,
        valid_row_count: validationState?.summary?.dataRows,
        invalid_row_count: validationState?.summary?.errorRows,
        total_rows: validationState?.summary?.totalRows,
        clean_rows: validationState?.summary?.cleanRows,
        file_size_bytes: pendingLocalFile?.size,
      });
      onConfirm?.();
      onFileValidated(fileForSubmit, validationMetadata);
    }
  };

  const handleBack = () => {
    trackEvent("validation_step_navigated", {
      dataset_id: datasetId,
      from_step: currentStep,
      to_step: currentStep - 1,
      direction: "back",
      ingestion_id: ingestion?.data_ingestion_id,
    });
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      clearError();
    }
  };

  const canProceed = (): boolean => {
    if (currentStep === 0) {
      return canStartValidation && !isLoading && !isUploading && !isLoadingIngestion;
    }
    if (currentStep === 1) {
      return (
        !isUploading && !!validationState?.isComplete && isProfilingComplete && !hasMajorFailure
      );
    }
    if (currentStep === 2) {
      return !validationState?.hasErrors && !profilingError;
    }
    return true;
  };

  const isStep0Busy = currentStep === 0 && isLoading;

  const getNextButtonLabel = (): string => {
    if (currentStep === 0) {
      return hasIngestion && !isStartingFresh ? "Next" : "Validate";
    }
    if (currentStep === 1) {
      return "Next";
    }
    if (currentStep === 2) {
      return "Select";
    }
    return "Next";
  };

  const getBackButtonLabel = (): string => {
    switch (currentStep) {
      case 1:
      case 2:
        return "Previous";
      default:
        return "Cancel";
    }
  };

  return (
    <div className="space-y-3">
      {/* Error display */}
      {error && (
        <div
          className={cn("p-3 rounded-lg text-sm border", "bg-red-50 border-red-200 text-red-700")}
        >
          {error}
        </div>
      )}

      {/* Navigation buttons — negative margins break out of DialogContent p-6 for full-bleed border and balanced spacing */}
      <div className="flex justify-end gap-3 pt-4 pb-2 border-t -mx-6 px-6 -mb-4">
        {currentStep > 0 ? (
          <Button variant="outline" onClick={handleBack} disabled={isStep0Busy}>
            {getBackButtonLabel()}
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => {
              const hadEngagement =
                !!selectedFile || !!pendingLocalFile || !!ingestion?.data_ingestion_id;
              if (hadEngagement) {
                trackEvent("file_validation_cancelled", {
                  dataset_id: datasetId,
                  had_file_selected: !!selectedFile || !!pendingLocalFile,
                  file_size_bytes: pendingLocalFile?.size,
                  ingestion_id: ingestion?.data_ingestion_id,
                });
              }
              onClose();
            }}
            disabled={isStep0Busy}
          >
            Cancel
          </Button>
        )}

        <Button onClick={handleNext} disabled={!canProceed()}>
          {getNextButtonLabel()}
        </Button>
      </div>
    </div>
  );
};
